import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import toast from 'react-hot-toast';
import { requestPasswordReset } from '~/services/auth';

type FormData = {
    email: string;
};

const ForgotPassword = () => {
    const navigate = useNavigate();
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [submittedEmail, setSubmittedEmail] = useState<string>('');

    const {
        handleSubmit,
        control,
        formState: { errors, isValid },
    } = useForm<FormData>({
        defaultValues: { email: '' },
        mode: 'onChange',
    });

    const onSubmit = async (data: FormData) => {
        setSubmitting(true);
        try {
            await requestPasswordReset(data.email);
            setSubmittedEmail(data.email);
        } catch (error) {
            console.error('Error requesting password reset:', error);
            toast.error("Couldn't send reset request. Please try again.");
            setSubmitting(false);
        }
    };

    if (submittedEmail) {
        return (
            <div className="flex h-screen flex-col items-center justify-center bg-gray-100">
                <div className="w-full max-w-md rounded-lg bg-white p-8 shadow-lg">
                    <h2 className="mb-6 text-center text-3xl font-bold text-gray-900">
                        Check your email
                    </h2>
                    <p className="mb-6 text-sm text-gray-600">
                        If an account exists for{' '}
                        <strong>{submittedEmail}</strong>, you&apos;ll receive a
                        password reset link shortly. The link expires in 24
                        hours.
                    </p>
                    <button
                        type="button"
                        onClick={() => navigate('/login')}
                        className="w-full rounded-lg bg-primary px-4 py-3 font-medium text-white transition hover:bg-opacity-90"
                    >
                        Return to Login
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="flex h-screen flex-col items-center justify-center bg-gray-100">
            <div className="w-full max-w-md rounded-lg bg-white p-8 shadow-lg">
                <h2 className="mb-6 text-center text-3xl font-bold text-gray-900">
                    Reset Password
                </h2>
                <p className="mb-6 text-sm text-gray-600">
                    Enter the email address associated with your account and
                    we&apos;ll send you a link to reset your password.
                </p>

                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                    <div>
                        <label
                            htmlFor="email"
                            className="mb-2 block text-sm font-medium text-gray-700"
                        >
                            Email
                        </label>
                        <Controller
                            name="email"
                            control={control}
                            rules={{
                                required: 'Email is required',
                                pattern: {
                                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                    message: 'Invalid email address',
                                },
                            }}
                            render={({ field }) => (
                                <input
                                    {...field}
                                    id="email"
                                    type="email"
                                    autoComplete="email"
                                    className="w-full rounded-lg border border-stroke bg-transparent px-4 py-3 outline-none focus:border-primary"
                                    placeholder="Enter your email"
                                />
                            )}
                        />
                        {errors.email && (
                            <p className="mt-1 text-sm text-red-500">
                                {errors.email.message}
                            </p>
                        )}
                    </div>

                    <button
                        type="submit"
                        disabled={submitting || !isValid}
                        className="w-full rounded-lg bg-primary px-4 py-3 font-medium text-white transition hover:bg-opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        {submitting ? 'Sending...' : 'Send reset link'}
                    </button>

                    <button
                        type="button"
                        onClick={() => navigate('/login')}
                        className="w-full rounded-lg border border-stroke px-4 py-3 font-medium text-gray-700 transition hover:bg-gray-50"
                    >
                        Return to Login
                    </button>
                </form>
            </div>
        </div>
    );
};

export default ForgotPassword;
